import type {ReactElement} from 'react';
import {useState} from 'react';
import React, {useEffect, useMemo} from 'react';
import {Text, TextSize} from '@Components/text';
import {useDownloadPanelData} from '@Components/download-panel/download-panel.context';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION, DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import styles from './download-select-pages-option.module.scss';
import {DropdownGhostSelectBox} from '@Components/dropdown-v2/components/dropdown-ghost-select-box';
import {Checkbox, CHECKBOX_TYPE} from '@Components/checkbox';
import {toggleInnerDropdownOpenState} from '@Components/download-panel/download-panel.reducer';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {isUserPAYG} from '@Libraries/user.library';
import {MAX_NUMBER_OF_DOWNLOADABLE_PAGES_FOR_PAYG_USERS} from '@Libraries/poster-download-library';
import {useUpdateMp4DurationFromSelectedPages} from '@Hooks/download-dialog/use-update-mp4-duration-from-selected-pages';
import {useUpdateMoreOptionsModal} from '@Components/modal/hooks/useUpdateMoreOptionsModal';

interface DownloadSelectPagesOptionProps {
  isOpenedInEditor?: boolean;
}

export function DownloadSelectPagesOption({isOpenedInEditor = true}: DownloadSelectPagesOptionProps): ReactElement | null {
  const {posterObject, selectedPages, setSelectedPages, thumbnails, fileType, setDuration} = useDownloadPanelData();
  const totalNumberOfPages = posterObject.pages.pageOrder.length;
  const [isDropdownOpened, setIsDropdownOpened] = useState(false);

  useUpdateMp4DurationFromSelectedPages(posterObject, selectedPages, fileType, setDuration);
  const isPAYGUser = isUserPAYG();
  const isPaygDownloadPagesLimitEnabled = isPAYGUser && totalNumberOfPages > MAX_NUMBER_OF_DOWNLOADABLE_PAGES_FOR_PAYG_USERS;
  const dispatch = useAppDispatch();
  const pagePreviews = useAppSelector((state) => state.posterEditor.pagePreviews);

  useEffect(() => {
    if (!isPaygDownloadPagesLimitEnabled) {
      return;
    }
    const firstAllowedPages = posterObject.pages.pageOrder.slice(0, MAX_NUMBER_OF_DOWNLOADABLE_PAGES_FOR_PAYG_USERS);
    const shouldForcePreselectFirstAllowed =
      selectedPages.length === 0 || selectedPages.length > MAX_NUMBER_OF_DOWNLOADABLE_PAGES_FOR_PAYG_USERS || selectedPages.length === totalNumberOfPages;

    if (shouldForcePreselectFirstAllowed) {
      setSelectedPages(firstAllowedPages);
    }
  }, [isPaygDownloadPagesLimitEnabled, posterObject.pages.pageOrder, selectedPages.length, setSelectedPages, totalNumberOfPages]);

  const thumbnailsArray = useMemo((): string[] => {
    if (isOpenedInEditor) {
      return posterObject.pages.pageOrder.map((pageId) => pagePreviews[pageId] || '');
    }
    if (thumbnails) {
      return posterObject.pages.pageOrder.map((pageId) => thumbnails[pageId] || '');
    }
    return [];
  }, [isOpenedInEditor, posterObject.pages.pageOrder, pagePreviews, thumbnails]);

  const getTextForSelector = (): string => {
    if (selectedPages.length === 0) {
      return window.i18next.t('pmwjs_none_0');
    }

    if (selectedPages.length === posterObject.pages.pageOrder.length) {
      return `${window.i18next.t('pmwjs_all')} (${selectedPages.length})`;
    }

    const selectedPageNumbers = selectedPages
      .map((pageId) => posterObject.pages.pageOrder.indexOf(pageId) + 1)
      .filter((pageNumber) => pageNumber > 0)
      .sort((a, b) => a - b);

    if (selectedPageNumbers.length === 0) {
      return '';
    }

    const ranges: string[] = [];
    let start = selectedPageNumbers[0];
    let end = selectedPageNumbers[0];

    for (let i = 1; i < selectedPageNumbers.length; i++) {
      const current = selectedPageNumbers[i];
      if (current === end + 1) {
        end = current;
        continue;
      }

      ranges.push(start === end ? `${start}` : `${start}-${end}`);
      start = current;
      end = current;
    }

    ranges.push(start === end ? `${start}` : `${start}-${end}`);
    return ranges.join(', ');
  };

  const showSelectorDangerState = (): boolean => {
    return selectedPages.length === 0;
  };

  const getSelector = (): ReactElement => {
    return (
      <DropdownGhostSelectBox
        className={showSelectorDangerState() && isDropdownOpened ? styles.dangerSelectBox : ''}
        textClassName={showSelectorDangerState() ? 'content-danger' : ''}
        iconClassName={showSelectorDangerState() ? styles.dangerIconColor : styles.defaultIconColor}
        size={DROPDOWN_SIZE.SMALL}
        icon="icon-pages"
        text={getTextForSelector()}
      />
    );
  };

  const getThumbnail = (src: string, isDisabled = false): ReactElement | null => {
    if (!src) {
      return null;
    }
    return (
      <div className={`${styles.thumbnailWrapper} ${isDisabled ? styles.disabled : ''} radius-4 border-s-standard`}>
        <img src={src} alt="" className={`${styles.img} _full-width _full-height`} />
      </div>
    );
  };

  const handleSelectAll = (): void => {
    if (isPaygDownloadPagesLimitEnabled) {
      const firstAllowedPages = posterObject.pages.pageOrder.slice(0, MAX_NUMBER_OF_DOWNLOADABLE_PAGES_FOR_PAYG_USERS);
      const isFirstAllowedSelected = selectedPages.length === firstAllowedPages.length && firstAllowedPages.every((pageId, idx) => selectedPages[idx] === pageId);

      if (isFirstAllowedSelected) {
        setSelectedPages([posterObject.pages.pageOrder[0]]);
      } else {
        setSelectedPages(firstAllowedPages);
      }
      return;
    }

    if (selectedPages.length === totalNumberOfPages) {
      setSelectedPages([posterObject.pages.pageOrder[0]]);
    } else {
      setSelectedPages([...posterObject.pages.pageOrder]);
    }
  };

  const handlePageToggle = (pageId: string): void => {
    if (selectedPages.includes(pageId)) {
      setSelectedPages(selectedPages.filter((page) => page !== pageId));
    } else {
      if (isPaygDownloadPagesLimitEnabled && selectedPages.length >= MAX_NUMBER_OF_DOWNLOADABLE_PAGES_FOR_PAYG_USERS) {
        return;
      }
      const next = [...selectedPages, pageId];
      next.sort((a, b) => posterObject.pages.pageOrder.indexOf(a) - posterObject.pages.pageOrder.indexOf(b));
      setSelectedPages(next);
    }
  };

  const firstAllowedPagesForPayg = isPaygDownloadPagesLimitEnabled ? posterObject.pages.pageOrder.slice(0, MAX_NUMBER_OF_DOWNLOADABLE_PAGES_FOR_PAYG_USERS) : [];
  const isSelectFirstTenChecked =
    isPaygDownloadPagesLimitEnabled && selectedPages.length === firstAllowedPagesForPayg.length && firstAllowedPagesForPayg.every((pageId) => selectedPages.includes(pageId));

  const getPopup = (): ReactElement => {
    return (
      <ul className="flex-v-row _full-width spacing-m-0">
        {selectedPages.length === 0 ? (
          <li className="spacing-m-b-2">
            <Text val={window.i18next.t('pmwjs_select_one_or_more_pages_to_download')} size={TextSize.XSMALL} className="ct-error-primary" />
          </li>
        ) : null}
        <li
          className={`flex-h-row ${styles.selectAllListItem} radius-8 hover-transition-all flex-items-center spacing-m-b-2 spacing-p-t-1 spacing-p-b-1 spacing-p-r-3 spacing-p-l-3 ${styles.listItem} _cursor-pointer`}
          key={'select-all'}
          onClick={handleSelectAll}
        >
          <Checkbox
            isChecked={isPaygDownloadPagesLimitEnabled ? isSelectFirstTenChecked : selectedPages.length === totalNumberOfPages}
            className="spacing-m-r-3"
            type={CHECKBOX_TYPE.PRIMARY}
          />
          <Text
            bold
            val={
              isPaygDownloadPagesLimitEnabled
                ? window.i18next.t('pmwjs_select_first_x_pages_from_y', {
                    pages: MAX_NUMBER_OF_DOWNLOADABLE_PAGES_FOR_PAYG_USERS,
                    totalPages: totalNumberOfPages,
                  })
                : window.i18next.t('pmwjs_select_all_pages', {
                    pages: totalNumberOfPages,
                  })
            }
            size={TextSize.XSMALL}
          />
        </li>
        {posterObject.pages.pageOrder.map((page, index) => {
          const pageNumber = index + 1;
          const isPageSelected = selectedPages.includes(page);
          const isDisabled = isPaygDownloadPagesLimitEnabled && selectedPages.length >= MAX_NUMBER_OF_DOWNLOADABLE_PAGES_FOR_PAYG_USERS && !isPageSelected;

          return (
            <li
              className={`flex-h-row radius-8 hover-transition-all flex-items-center spacing-m-b-2 spacing-p-t-1 spacing-p-b-1 spacing-p-r-3 spacing-p-l-3 ${styles.listItem} ${isDisabled ? styles.disabled : '_cursor-pointer'}`}
              key={page}
              onClick={(): void => {
                if (!isDisabled) {
                  handlePageToggle(page);
                }
              }}
            >
              <Checkbox isChecked={isPageSelected} disabled={isDisabled} className="spacing-m-r-3" type={CHECKBOX_TYPE.PRIMARY} />
              {getThumbnail(thumbnailsArray[index] || '', isDisabled)}
              <Text bold val={`${window.i18next.t('pmwjs_page')} ${pageNumber}`} className={`spacing-m-l-3 ${isDisabled ? 'content-disabled' : ''}`} size={TextSize.XSMALL} />
            </li>
          );
        })}
      </ul>
    );
  };

  const popupContent = getPopup();
  useUpdateMoreOptionsModal(popupContent, [selectedPages, thumbnailsArray, isPaygDownloadPagesLimitEnabled]);

  const toggleStateInRedux = (state: boolean): void => {
    dispatch(toggleInnerDropdownOpenState(state));
  };

  if (posterObject.pages.pageOrder.length === 1) {
    return null;
  }

  return (
    <div className="_full-width flex-h-row justify-content-between flex-items-center">
      <Text bold val={window.i18next.t('pmwjs_pages')} size={TextSize.XSMALL} />
      <Dropdown
        onOpen={(): void => {
          toggleStateInRedux(true);
          setIsDropdownOpened(true);
        }}
        onClose={(): void => {
          toggleStateInRedux(false);
          setIsDropdownOpened(false);
        }}
        alignment={ALIGNMENT_TYPE.BOTTOM_END}
        maxHeight={380}
        showModalForSmallScreen
        position={DROPDOWN_POSITION.FIXED}
        selector={getSelector()}
        popupClassName={styles.popup}
        popUpHasCustomWidth
        popup={popupContent}
        popupSpacingClass="spacing-p-3"
        doCloseOnInnerClick={false}
        modalTitle={window.i18next.t('pmwjs_select_pages')}
      />
    </div>
  );
}
